/* eslint-disable max-len */
/* eslint-disable no-extra-parens */
import { saveAs } from 'file-saver';
import ReactTooltip from 'react-tooltip';
import JsonQuery from 'json-query';
import _ from 'lodash';
import React, { useState } from 'react';
import type { CellObject, ColInfo, Range, RowInfo, WorkBook, WorkSheet } from 'xlsx-js-style';
import { hooks as commonHooks } from '@sitebuilder/common';
import { getTextFromHTML } from '../utils';
import { AnnotationFilters } from '../Annotations/Annotations';
import TableHeadCell from './TableHeadCell';
import TableRow from './TableRow';
import TableRowGroup from './TableRowGroup';
import {
    DownloadFileOptions,
    ExcelColumnOptions,
    FirstColumnOption,
    SheetTabOption,
    View,
} from './utils';

export interface PropsShape {
    iconShowContent?: string;
    iconHideContent?: string;
    useIconForFavoriteColumn?: string;
    favoriteColumnTooltipTextColor?: string;
    favoriteColumnTooltipBgColor?: string;
    firstColumnOptions?: FirstColumnOption;
    columns: {
        id: string;
        label: string;
        format?: string;
        disableSorting: boolean;
        nullGuard?: string;
        noDataAnnotationId?: string;
        annotationId?: string;
        columnType?: 'risk' | 'price' | 'rating' | 'documents' | 'twoRow' | 'checklist';
        columnGrouping?: string;
        hideColumn?: boolean;
        excelOptions?: ExcelColumnOptions;
    }[];
    columnGroups?: {
        id: string;
        label: string;
    }[];
    rows: Record<string, string>[];
    grouping: {
        groups: { value: string; label: string }[];
        propertyId: string;
    } | null;
    sortState: { sortProperty: string; isAscending: boolean } | null;
    onToggleSort: (propertyId: string) => void;
    checkList: string[];
    setCheckList: (checkList: string[]) => void;
    fundUrlMapping: {
        parameters: {
            key: string;
            value: string;
        }[];
        fundUrlTag: string;
    }[];
    fundUrl: string;
    fundUrlBase: string;
    fundPdfUrlBase: string;
    fundPdfName: string;
    filterFavorites: boolean;
    setFilterFavorites: (checked: boolean) => void;
    setFavorites: (favorites: string[]) => void;
    favorites: string[];
    dataContext: any;
    selectedView: View;
    downloadFileOptions: DownloadFileOptions;
    data?: Record<string, unknown>;
    selectedListStorageName?: string;
    selectedListDataId?: string;
    maxSelectionList?: number;
    selectedListJsonPath?: string;
    disableCheckbox?: boolean;
    setDisableCheckbox: (checked: boolean) => void;
    isInformationFolderVisible: boolean;
    disableTabStaticData: boolean;
}

interface Column {
    id: string;
    label: string;
    format?: string;
    nullGuard?: string;
    noDataAnnotationId?: string;
    annotationId?: string;
    disableSorting: boolean;
    columnType?: 'risk' | 'price' | 'rating' | 'documents' | 'twoRow' | 'checklist';
    columnGrouping?: string;
}

export default function Table(props: PropsShape) {
    const {
        columns,
        rows,
        grouping,
        checkList,
        setCheckList,
        firstColumnOptions,
        fundUrlMapping,
        fundUrlBase,
        fundPdfUrlBase,
        fundPdfName,
        columnGroups,
        dataContext,
        selectedView,
        useIconForFavoriteColumn,
        favoriteColumnTooltipTextColor,
        favoriteColumnTooltipBgColor,
        setDisableCheckbox,
        isInformationFolderVisible,
        ...rest
    } = props;
    const { fileType, excelTabs, csvOptions } = rest?.downloadFileOptions || {};
    let tbodyElements;
    let rowsDataToDownload: any;

    const i18n = commonHooks.useLocalization();

    const [selectedColumnGroup, setSelectedColumnGroup] = useState<string | null>(null);
    const annotator = commonHooks.useAnnotator();

    const doColumnsHaveGrouping = columns.some(
        (c) =>
            c.columnGrouping && columnGroups && columnGroups.find((g) => g.id === c.columnGrouping)
    );

    if (doColumnsHaveGrouping && columnGroups && columnGroups.length && !selectedColumnGroup) {
        setSelectedColumnGroup(columnGroups[0]?.id);
    }

    const filteredColumns = columns.filter((c) => !c?.hideColumn);

    let processedColumns = doColumnsHaveGrouping
        ? filteredColumns.filter(
              (c) => !c.columnGrouping || c.columnGrouping === selectedColumnGroup
          )
        : filteredColumns;

    processedColumns = processedColumns.map((c) => {
        if (c.disableSorting) {
            return c;
        }

        c.disableSorting = true;
        let cellData: any = null;

        rows.some((r) => {
            const dataPoint = r[c.id];

            if (cellData === null) {
                cellData = dataPoint;
            } else if (cellData !== dataPoint) {
                c.disableSorting = false;
                return true;
            }

            return false;
        });

        return c;
    });

    const onCheckAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setCheckList(rows.map((r) => r.id));
        } else {
            setCheckList([]);
        }
    };

    const onSelectFavorites = (e: React.ChangeEvent<HTMLInputElement>) => {
        rest.setFilterFavorites(e.target.checked);
    };

    const onEnterFavorites = (e: React.KeyboardEvent<HTMLLabelElement>) => {
        if (e.key === 'Enter') {
            rest.setFilterFavorites(!rest.filterFavorites);
        }
    };

    if (rows.length < 1) {
        // No rows to show
        tbodyElements = (
            <tr>
                <td colSpan={processedColumns.length}>{i18n.t('widgets_fundList_noRows')}</td>
            </tr>
        );
        rowsDataToDownload = i18n.t('widgets_fundList_noRows');
    } else if (grouping && grouping.groups.length > 0) {
        // Display rows grouped by specified property
        tbodyElements = grouping.groups.map((o) => {
            const processedRows = rows.filter((r) => r[grouping.propertyId] === o.value);

            if (processedRows.length > 0) {
                return (
                    <TableRowGroup
                        key={o.value}
                        id={o.value}
                        label={o.label || o.value}
                        firstColumnOptions={firstColumnOptions}
                        columns={processedColumns}
                        rows={processedRows}
                        checkList={checkList}
                        setCheckList={setCheckList}
                        iconShowContent={rest.iconShowContent}
                        iconHideContent={rest.iconHideContent}
                        fundUrlMapping={fundUrlMapping}
                        fundUrl={rest.fundUrl}
                        fundUrlBase={fundUrlBase}
                        fundPdfUrlBase={fundPdfUrlBase}
                        fundPdfName={fundPdfName}
                        setFavorites={rest.setFavorites}
                        dataContext={dataContext}
                        selectedListStorageName={rest.selectedListStorageName}
                        selectedListDataId={rest.selectedListDataId}
                        maxSelectionList={rest.maxSelectionList}
                        selectedListJsonPath={rest.selectedListJsonPath}
                        disableCheckbox={rest.disableCheckbox}
                        setDisableCheckbox={setDisableCheckbox}
                        disableTabStaticData={rest.disableTabStaticData}
                    />
                );
            }

            return null;
        });
        rowsDataToDownload = grouping?.groups.map((o) => {
            return rows.filter((r) => r[grouping.propertyId] === o.value);
        });
    } else {
        // No grouping, just flat list of rows
        tbodyElements = rows.map((o) => (
            <TableRow
                key={o.id}
                firstColumnOptions={firstColumnOptions}
                columns={processedColumns}
                data={o}
                checkList={checkList}
                setCheckList={setCheckList}
                fundUrlMapping={fundUrlMapping}
                fundUrl={rest.fundUrl}
                fundUrlBase={fundUrlBase}
                fundPdfUrlBase={fundPdfUrlBase}
                fundPdfName={fundPdfName}
                setFavorites={rest.setFavorites}
                dataContext={dataContext}
                selectedListStorageName={rest.selectedListStorageName}
                selectedListDataId={rest.selectedListDataId}
                maxSelectionList={rest.maxSelectionList}
                selectedListJsonPath={rest.selectedListJsonPath}
                disableCheckbox={rest.disableCheckbox}
                setDisableCheckbox={setDisableCheckbox}
                disableTabStaticData={rest.disableTabStaticData}
            />
        ));
        rowsDataToDownload = rows;
    }

    const onDownloadClick = () => {
        const flattenedRowsData = _.isArray(rowsDataToDownload) ? rowsDataToDownload.flat() : [];

        // Initialize workbook
        import('xlsx-js-style').then(({ utils, write }) => {
            const wb: WorkBook = utils?.book_new();
            const tabs: SheetTabOption[] = fileType === '.xlsx' ? excelTabs : [csvOptions];

            // Append inception dates to where applicable
            let inceptionDate: string;

            if (selectedView.id === 'overview' || selectedView.id === 'fundCodes') {
                inceptionDate = '';
            } else {
                inceptionDate = ` ${i18n.format(
                    selectedView.subHeading || '',
                    dataContext
                )}`.replace('As', 'as');
            }

            // process each tabs options
            tabs?.forEach((opt: SheetTabOption) => {
                const rowsData: any = []; // worksheet data
                const wsCols: ColInfo[] = []; // worksheet column options
                const wsRows: RowInfo[] = []; // worksheet row options
                const wsMerges: Range[] = []; // worksheet merges options
                let firstColumnLabel: string = '';
                flattenedRowsData?.forEach((r: any) => {
                    const row: any = {};
                    columns
                        ?.filter((c) => !!c?.excelOptions?.showColumn)
                        ?.forEach((c: any, _index: number) => {
                            const { excelLabel, dataKey, format, columnType } =
                                c?.excelOptions || {};
                            const label = i18n.format(excelLabel || c.label, dataContext) || '';

                            if (_index === 0) {
                                firstColumnLabel = label;
                            }

                            let value: any = '—';

                            if (typeof r?.[c.id] === 'object' && dataKey) {
                                value =
                                    JsonQuery(`${dataKey}`, {
                                        data: { ...r?.[c.id], ...dataContext },
                                    })?.value || '—';
                            } else if (columnType === 'price') {
                                value =
                                    r?.[c.id] > 0
                                        ? i18n.t('widgets_fundList_up')
                                        : i18n.t('widgets_fundList_down');
                            } else {
                                let noDataMarker: any = '';

                                if (
                                    !r?.[c.id] &&
                                    c.nullGuard &&
                                    c.noDataAnnotationId &&
                                    !_.isEmpty(annotator)
                                ) {
                                    try {
                                        // eslint-disable-next-line max-len
                                        noDataMarker = annotator?.markersByMarkerTargetId(
                                            `${c?.noDataAnnotationId}`
                                        );
                                    } catch (e) {
                                        console.log(e);
                                    }
                                }

                                value =
                                    r?.[c.id] ||
                                    `${i18n.format(c.nullGuard, dataContext)} ${getTextFromHTML(
                                        noDataMarker
                                    )}` ||
                                    '_';
                            }

                            if (value && (format || c.format)) {
                                value = i18n.t(value as any, format || c.format) || '—';
                            }

                            row[label] = value;
                            const width =
                                label?.length >= value?.length ? label?.length : value?.length;
                            wsCols[_index] = {
                                wch: width > 10 ? width + 5 : 10,
                            };
                        });
                    // add rows to worksheet
                    rowsData.push(row);
                });

                const disclaimers: string[] = [];

                // check if disclaimer should show or not
                if (opt.showDisclaimer && opt?.disclaimers?.length) {
                    // add disclaimer to worksheet
                    // filterBy is deprecated and can be removed once no longer in use
                    opt?.disclaimers?.forEach(
                        (d: {
                            filterBy: string;
                            filters: AnnotationFilters;
                            showMarker: boolean;
                        }) => {
                            const annotation = annotator.getAnnotationsX(
                                d.filterBy,
                                d.filters,
                                rest.data,
                                i18n
                            );
                            const disclaimer = d.showMarker
                                ? getTextFromHTML(annotation) || ''
                                : getTextFromHTML(
                                      annotation.replace(/<sup>(.+?)<\/sup>/, '')
                                  )?.trim() || '';
                            disclaimers.push(disclaimer);
                            rowsData[rowsData.length + 1] = { [firstColumnLabel]: disclaimer };

                            // merge worksheet cells for disclaimer
                            const merge: Range = {
                                s: { r: rowsData.length, c: 0 },
                                e: { r: rowsData.length, c: columns.length - 1 },
                            };
                            // calculate row height for disclaimer
                            const height = disclaimer.toString().length / columns.length + 10;
                            wsRows[rowsData.length] = {
                                hpx: height > 15 ? height : 15,
                            };
                            wsMerges.push(merge);
                        }
                    );
                }

                const ws: WorkSheet = utils?.json_to_sheet(rowsData);
                if (ws) {
                    ws['!cols'] = wsCols;
                    ws['!merges'] = wsMerges;
                    ws['!rows'] = wsRows;

                    // apply style to worksheet
                    for (const i in ws as CellObject) {
                        if (typeof ws[i] === 'object') {
                            ws[i].s = {
                                alignment: {
                                    horizontal: 'left', // align text to right
                                },
                            };

                            if (disclaimers.includes(ws[i].v)) {
                                ws[i].s = {
                                    alignment: {
                                        wrapText: true,
                                    },
                                };
                            }
                        }
                    }
                }

                const tabName = opt?.name
                    ? `${i18n.format(opt?.name, dataContext)}`
                    : `${i18n.format(selectedView.label, dataContext)}`;
                // append each sheet to workbook
                utils?.book_append_sheet(wb, ws, `${tabName}${inceptionDate}`);
            });
            let excelBuffer: string = '';
            if (wb) {
                excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
            }

            // check file type for worksheet
            if (fileType === '.xlsx') {
                const _data = new Blob([excelBuffer], { type: fileType });
                saveAs(
                    _data,
                    `${i18n.format(selectedView.label, dataContext)}${inceptionDate}.xlsx` ||
                        'download.xlsx'
                );
            } else if (fileType === '.csv') {
                // read xlsx file
                import('xlsx').then(({ read }) => {
                    // use XLSX here
                    const rs = read(excelBuffer, { type: 'file' });
                    // generate csv file
                    const csvBuffer: string = write(rs, { bookType: 'csv', type: 'array' });
                    const blobCSV = new Blob([csvBuffer], { type: 'text/plain;charset=UTF-8' });
                    saveAs(
                        blobCSV,
                        `${i18n.format(selectedView.label, dataContext)}${inceptionDate}.csv` ||
                            'download.csv'
                    );
                });
            }
        });
    };

    const columnsBeforeGrouping: Column[] = [];
    const groupedColumns: Column[] = [];
    const columnsAfterGrouping: Column[] = [];

    if (doColumnsHaveGrouping) {
        let groupedColumnsAreStored = false;

        processedColumns.forEach((c) => {
            if (
                c.columnGrouping &&
                columnGroups &&
                columnGroups.find((g) => g.id === c.columnGrouping)
            ) {
                groupedColumns.push(c);
                groupedColumnsAreStored = true;
            } else {
                groupedColumnsAreStored
                    ? columnsAfterGrouping.push(c)
                    : columnsBeforeGrouping.push(c);
            }
        });
    }

    return (
        <div className="fund-list-table-wrapper">
            <div className="fund-list-table-actions sticky-header">
                <button
                    className="download"
                    onClick={() => onDownloadClick()}
                    data-testid="download-button-main"
                    disabled={rows.length < 1}
                >
                    <span className="excel">{i18n.t('widgets_fundList_excel')}</span>
                    <span className="download-label">{i18n.t('widgets_fundList_download')}</span>
                </button>

                {selectedView?.subHeading && (
                    <div tabIndex={rest.disableTabStaticData ? -1 : 0}>
                        {i18n.format(selectedView.subHeading || '', dataContext)}
                    </div>
                )}
            </div>
            <table id="fund-list-table" className="fund-list-table">
                <thead
                    className={`sticky-header ${
                        isInformationFolderVisible ? 'information-visible' : ''
                    }`}
                >
                    {doColumnsHaveGrouping && (
                        <tr role="row">
                            {columnsBeforeGrouping.length && (
                                <th colSpan={columnsBeforeGrouping.length + 1} />
                            )}

                            {columnGroups && (
                                <th
                                    colSpan={groupedColumns.length}
                                    className="column-groups"
                                    aria-label={columnGroups
                                        .map((g) => i18n.format(g.label, dataContext))
                                        .join(', ')}
                                >
                                    {columnGroups.map((g) => (
                                        <button
                                            key={g.id}
                                            className={`column-group-toggle ${
                                                selectedColumnGroup === g.id ? 'selected' : ''
                                            }`}
                                            onClick={() => setSelectedColumnGroup(g.id)}
                                            tabIndex={0}
                                            aria-hidden={true}
                                        >
                                            {i18n.format(g.label, dataContext)}
                                        </button>
                                    ))}
                                </th>
                            )}

                            {columnsAfterGrouping.length && (
                                <th colSpan={columnsAfterGrouping.length} />
                            )}
                        </tr>
                    )}
                    <tr data-testid="first-row" role="row">
                        {firstColumnOptions?.type !== 'hide column' && (
                            <th data-testid="first-column">
                                {firstColumnOptions?.type === 'checklist' ? (
                                    <input
                                        type="checkbox"
                                        onChange={onCheckAllChange}
                                        checked={checkList.length === rows.length}
                                    />
                                ) : (
                                    firstColumnOptions?.type === 'favorites' && (
                                        <span className="favorites">
                                            <input
                                                id="favorites"
                                                type="checkbox"
                                                onChange={onSelectFavorites}
                                                checked={rest.filterFavorites}
                                                aria-label={`${i18n.t(
                                                    'widgets_fundList_columnHeadings_favorites'
                                                )} ${rest?.favorites?.length}`}
                                            />
                                            <label
                                                className="favorites-label"
                                                htmlFor="favorites"
                                                tabIndex={0}
                                                onKeyDown={onEnterFavorites}
                                            >
                                                {useIconForFavoriteColumn ? (
                                                    <>
                                                        <i
                                                            className={useIconForFavoriteColumn}
                                                            data-tip
                                                            data-for="favorite-tooltip"
                                                            data-text-color={
                                                                favoriteColumnTooltipTextColor ||
                                                                '#fff'
                                                            }
                                                            data-background-color={
                                                                favoriteColumnTooltipBgColor ||
                                                                '#000'
                                                            }
                                                        />
                                                        <ReactTooltip
                                                            id="favorite-tooltip"
                                                            place="top"
                                                            effect="solid"
                                                            className="favorite-tooltip"
                                                        >
                                                            {i18n.t(
                                                                'widgets_fundList_columnHeadings_favorites'
                                                            )}
                                                        </ReactTooltip>
                                                    </>
                                                ) : (
                                                    i18n.t(
                                                        'widgets_fundList_columnHeadings_favorites'
                                                    )
                                                )}
                                                <span className="sr-only">{`${i18n.t(
                                                    'widgets_fundList_columnHeadings_favorites'
                                                )}`}</span>
                                                ({rest?.favorites?.length})
                                            </label>
                                        </span>
                                    )
                                )}
                            </th>
                        )}
                        {processedColumns
                            ?.filter((c) => !c?.hideColumn)
                            .map((o) => (
                                <TableHeadCell
                                    key={o.id}
                                    column={o}
                                    sortState={props.sortState}
                                    onToggleSort={props.onToggleSort}
                                    dataContext={dataContext}
                                />
                            ))}
                    </tr>
                </thead>
                <tbody>{tbodyElements}</tbody>
            </table>
        </div>
    );
}
